import type { ReactNode } from "react"
import { ArrowLeft, Lightbulb } from "lucide-react"
import { cn } from "@/lib/utils"

// Briques visuelles partagées par NutritionPage et WeeklyMealPlan (maquette « Nutrition v2 »).

// Halo violet d'ambiance, fixe derrière le contenu.
export function NutritionBackdrop() {
  return (
    <div
      aria-hidden
      className="fixed inset-0 z-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(70% 45% at 85% 0%, rgba(139,92,246,0.14), transparent 70%), radial-gradient(60% 40% at 0% 30%, rgba(139,92,246,0.06), transparent 70%)",
      }}
    />
  )
}

interface NutritionHeaderProps {
  title: string
  onBack: () => void
  children?: ReactNode
}

// Bouton retour + titre ; `children` (pastille de plan…) s'aligne sous le titre.
export function NutritionHeader({ title, onBack, children }: NutritionHeaderProps) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        onClick={onBack}
        aria-label="Retour"
        className="w-9 h-9 mt-0.5 rounded-xl border border-white/[0.08] bg-white/[0.04] flex items-center justify-center shrink-0 hover:bg-white/[0.08] transition-colors cursor-pointer"
      >
        <ArrowLeft className="w-4 h-4 text-gray-200" />
      </button>
      <div className="min-w-0">
        <h1 className="text-[24px] font-extrabold text-white tracking-tight leading-tight">{title}</h1>
        {children && <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">{children}</div>}
      </div>
    </div>
  )
}

export function PlanPill({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-400/35 bg-violet-500/10 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-violet-200">
      <span className="text-violet-400">Plan</span>
      <span className="text-violet-400/60">·</span>
      {label}
    </span>
  )
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 ml-1 mb-2.5">
      <span className="w-3 h-px bg-violet-400" />
      <span className="font-mono text-[10.5px] font-bold uppercase tracking-[0.18em] text-gray-400">{children}</span>
    </div>
  )
}

interface IconTileProps {
  color: string // hex à 6 chiffres (suffixe d'opacité ajouté)
  className?: string
  children: ReactNode
}

// Conteneur carré teinté pour une icône ou un emoji.
export function IconTile({ color, className, children }: IconTileProps) {
  return (
    <div
      className={cn("w-10 h-10 rounded-xl flex items-center justify-center shrink-0", className)}
      style={{ background: `${color}1f`, border: `1px solid ${color}40` }}
    >
      {children}
    </div>
  )
}

export function InfoBanner({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 rounded-2xl border border-amber-400/20 bg-amber-500/[0.06] px-3.5 py-3">
      <Lightbulb className="w-4 h-4 text-amber-400 shrink-0 mt-px" />
      <p className="text-[11.5px] text-gray-400 leading-relaxed">{children}</p>
    </div>
  )
}
